import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  GreenBtn,
  GreyBtnDisabled,
  WhiteGreyBtnSearching,
} from 'Stories/Button/Button.stories'
import { RootStateOrAny, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'
import { getAllPlaylistsSpotifyIds } from 'Redux/selectors/playLists'
import { getUserId } from 'Redux/selectors/auth'
import { playlistAddedEvent } from 'Helpers/tagManager'
import { AlertErrorMessageWithIcon } from '../../../../Shared/components'
import { findPlaylist, onAddPlaylist, cutPlaylistId } from '../helper'
import { CampaignPlatformTypeEnum } from '../../../../Types'

import { AddPlaylistFormProps } from './props'
import * as S from './styles'

const AddPlaylistForm: FunctionComponent<AddPlaylistFormProps> = ({
  toggle,
  isLoading,
  setIsLoading,
  setRecentlyAddedPlaylist,
  onCheckPlaylist,
}) => {
  const { t } = useTranslation()
  const userId = useSelector(getUserId)

  const accessToken: string = useSelector(
    (state: RootStateOrAny) => state.spotifyToken.accessToken
  )

  const spotifyId = useSelector(
    (state: RootStateOrAny) => state.auth.user.spotifyId
  )

  const allPlaylistsSpotifyIds = useSelector(getAllPlaylistsSpotifyIds)

  const [searchValue, setSearchValue] = useState<string>('')

  const [error, setError] = useState<string>('')

  const debouncedSearchValue = useDebounce(searchValue, 500)

  const eventHandler = (response: {
    id: string
    curator_id: number
    meets_conditions: boolean
  }) => {
    playlistAddedEvent(
      response.meets_conditions,
      CampaignPlatformTypeEnum.SPOTIFY,
      response.id,
      userId
    )
  }
  const onClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    onAddPlaylist({
      onCheckPlaylist,
      setRecentlyAddedPlaylist,
      searchValue,
      // @ts-ignore
      eventHandler,
    })
    toggle()
  }

  const findPlaylistHandler = () =>
    findPlaylist({
      accessToken,
      searchValue,
      setIsLoading,
      spotifyId,
      setError,
      allPlaylistsSpotifyIds,
    })

  useEffect(() => {
    if (
      debouncedSearchValue[0] &&
      !debouncedSearchValue[0].match(/playlist/g)
    ) {
      setIsLoading(false)
      setError('validation.incorrectLink')
      return
    }
    if (debouncedSearchValue[0]) {
      findPlaylistHandler()
    } else {
      setIsLoading(false)
      setError('')
    }
  }, [debouncedSearchValue[0]])

  useEffect(() => {
    searchValue && setIsLoading(true)
  }, [searchValue])

  useEffect(() => {
    isLoading && setError('')
  }, [isLoading])

  const errErrorWhenButtonDisable = [
    'validation.incorrectLink',
    'playlistsPage.emptyPlaylistNameError',
    'validation.notYourPlaylist',
    'validation.playlistAdded',
  ]

  const currentButton = useMemo(() => {
    if (isLoading) {
      return (
        <WhiteGreyBtnSearching
          color="whiteGreyDisabled"
          // @ts-ignore
          disabled
        >
          {t('playlistsPage.searching')}
        </WhiteGreyBtnSearching>
      )
    }
    if (!debouncedSearchValue[0] || errErrorWhenButtonDisable.includes(error)) {
      return (
        <GreyBtnDisabled
          color="greyDisabled"
          // @ts-ignore
          disabled
        >
          {t('playlistsPage.addPlaylist')}
        </GreyBtnDisabled>
      )
    }
    return (
      <GreenBtn
        color="green"
        type="submit"
        // @ts-ignore
        onClick={onClickHandler}
      >
        {t('playlistsPage.addPlaylist')}
      </GreenBtn>
    )
  }, [isLoading, error])

  const meetConditionsLink =
    'http://help.soundcamps.com/en/articles/5541325-requirements-for-your-playlists'

  return (
    <form className="theme-light form spotify_url_form">
      <S.FormContainer>
        <div>
          <S.Label>{t('playlistsPage.enterPlaylist')}</S.Label>
          <S.FieldContainer>
            <S.Placeholder>
              {t('playlistsPage.spotifyPlaylistURLorURI')}
            </S.Placeholder>
            <S.InputField
              value={searchValue}
              type="text"
              onChange={e => setSearchValue(e.target.value)}
            />
            <S.AlertWrapper>
              <AlertErrorMessageWithIcon
                type="desktop"
                isShow={!isLoading && !!error}
                errorText={t(error)}
              />
              {error === 'validation.notRequirementsPlaylist' && (
                <S.MeetConditionsBtn href={meetConditionsLink} target="_blank">
                  {t('validation.minimalPlatformRequirements')}
                </S.MeetConditionsBtn>
              )}
            </S.AlertWrapper>
          </S.FieldContainer>
        </div>
        <S.ButtonContainer>
          <S.Button>{currentButton}</S.Button>
        </S.ButtonContainer>
      </S.FormContainer>
    </form>
  )
}

export default AddPlaylistForm
